import React, { useEffect, useState, useContext } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, X } from "lucide-react";
import vybogTalIcon from "../../Assets/Images/VybogTAL-Chat-Icon.svg";
import "./ChatPopup.css";
import lightBulbIcon from "../../Assets/Images/match-light-bulb.svg";
import { DynamicContext } from "../../Context/DynamicContext";

function ChatPopup({
  onClose = (selectedSuggestion = null) => {},
  onSuggestionClick = () => {},
  title = "How to use VybogTAL Chat?",
  subtitle = "Ask questions about candidates to get insights, comparisons, and recommendations.",
  suggestions = [
    "Who is the best fit for this role?",
    "Compare selected candidates",
    "Summarize this candidate",
    "What skills are missing for this role?",
    "Who should I interview first?",
    "Why is this candidate a Partial Fit?",
  ],
  tipText = "Tip: Select one or more candidates for more accurate and relevant answers.",
  buttonText = "Got it",
  logo = vybogTalIcon,
  tipIcon = lightBulbIcon,
  isDarkMode: isDarkModeProp,
}) {
  const { isDarkMode: contextIsDarkMode } = useContext(DynamicContext) || {};
  const isDarkMode = isDarkModeProp ?? contextIsDarkMode;
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyWidth = body.style.width;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    documentElement.style.overflow = "hidden";

    return () => {
      body.style.overflow = previousBodyOverflow;
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.width = previousBodyWidth;
      documentElement.style.overflow = previousHtmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleSuggestionClick = (suggestion) => {
    setSelectedSuggestion(suggestion);
    onSuggestionClick(suggestion); 
  };

  const popupContent = (
    <div className={`vybog-chat-popup-overlay ${isDarkMode ? "dark-mode" : ""}`} role="presentation">
      <section
        className={`vybog-chat-popup ${isDarkMode ? "dark-mode" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vybog-chat-popup-title"
      >
        <button
          type="button"
          className="vybog-chat-popup-close"
          aria-label="Close"
          onClick={() => onClose(null)}
        >
          <X size={22} strokeWidth={2.6} />
        </button>

        <img
          src={logo}
          alt="VybogTAL"
          className="vybog-chat-popup-logo"
        />

        <h2 id="vybog-chat-popup-title">{title}</h2>
        <p className="vybog-chat-popup-subtitle">
          {subtitle}
        </p>

        <div className="vybog-chat-popup-divider">
          <span>Try asking</span>
        </div>

        <div className="vybog-chat-popup-suggestions">
          {suggestions.map((suggestion) => (
            <button
              type="button"
              className={`vybog-chat-popup-suggestion ${selectedSuggestion === suggestion ? 'selected' : ''}`}
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <span>{suggestion}</span>
              <ArrowRight size={21} strokeWidth={2.2} />
            </button>
          ))}
        </div>

        <div className="vybog-chat-popup-tip">
          <img src={tipIcon} alt="Tip" style={{ width: 22, height: 22 }} />
          <span>{tipText}</span>
        </div>

        <button
          type="button"
          className="vybog-chat-popup-got-it"
          onClick={() => onClose(selectedSuggestion)} 
          disabled={selectedSuggestion === null} 
        >
          {buttonText}
        </button>
      </section>
    </div>
  );

  if (typeof document === "undefined") {
    return popupContent;
  }

  return createPortal(popupContent, document.body);
}

export default ChatPopup;
